/**
 * Level 37 — the Poolrooms.
 *
 * The one level that is not trying to frighten anyone. Big tiled chambers on a
 * loose grid, joined by wide arched openings, with rectangular pools set into
 * the floor and causeways of dry tile between them. The whole map is mirrored
 * about its centre line, which is what makes it read as architecture instead of
 * as a maze — and, quietly, what makes it impossible to tell which half you are
 * standing in.
 *
 * Every pool is a clean rectangle registered as a 'flooded' region, so the
 * renderer can lay one water plane per region and have it line up exactly with
 * the Cell.Water it covers.
 */

import { Cell } from '../../core/types';
import { dampenWaterEdges, scatterOpenCeilings } from './decorate';
import {
  addRegion,
  fillRect,
  inset,
  interiorRect,
  pillarLattice,
  strokeRect,
  type GenSite,
  type Rect,
} from './site';
import { applyWrongness } from './wrongness';

/** Smallest chamber dimension in cells. */
const MIN_CHAMBER = 8;

/** Carve the Poolrooms profile into `site`. */
export function generatePoolrooms(site: GenSite): void {
  const { grid, rng, spec } = site;
  const bounds = interiorRect(grid);
  const mid = grid.w >> 1;

  const cols = bandsAcross(site, bounds.x, mid - 1, 9, 20);
  const rows = bandsAcross(site, bounds.y, bounds.y + bounds.h - 1, 9, 22);

  const chambers: Rect[] = [];
  for (const row of rows) {
    for (const col of cols) {
      const r: Rect = { x: col.a, y: row.a, w: col.b - col.a + 1, h: row.b - row.a + 1 };
      fillRect(grid, r, Cell.Floor);
      chambers.push(r);
    }
  }

  // Arched openings in every shared wall: this level should never feel like a
  // decision, only like a view.
  for (let j = 0; j < rows.length; j++) {
    for (let i = 0; i < cols.length; i++) {
      const r = chambers[j * cols.length + i]!;
      if (i + 1 < cols.length && rng.chance(0.92)) {
        const next = chambers[j * cols.length + i + 1]!;
        arch(site, r.x + r.w, Math.max(r.y, next.y), Math.min(r.y + r.h, next.y + next.h), true);
      }
      if (j + 1 < rows.length && rng.chance(0.92)) {
        const next = chambers[(j + 1) * cols.length + i]!;
        arch(site, r.y + r.h, Math.max(r.x, next.x), Math.min(r.x + r.w, next.x + next.w), false);
      }
    }
  }

  const depth = spec.waterDepth ?? 0;
  for (const r of chambers) dressChamber(site, r, depth);

  // Before the mirror, and confined to the half that gets built, so whatever
  // goes wrong here goes wrong identically on the other side. A room with a
  // sealed block in it is odd; the same room with the same block in it forty
  // metres away, and no way to tell which one you are standing in, is the level.
  applyWrongness(site, {
    innerRooms: [2, 4],
    doorsToNothing: [2, 4],
    perforatedWalls: [0, 1],
    pillarsInDoorways: [1, 2],
    wallFins: [3, 6],
    tightPillarPatches: [1, 2],
    bounds: { x: 1, y: 1, w: mid - 1, h: grid.h - 2 },
  });

  mirrorHalf(site, mid);

  scatterOpenCeilings(site, rng.int(2, 4), 2, 4);
  dampenWaterEdges(site, 0.4);
}

/** Bands of varying width covering [from, to] with a one-cell wall between each. */
function bandsAcross(site: GenSite, from: number, to: number, min: number, max: number): Array<{ a: number; b: number }> {
  const { rng } = site;
  const out: Array<{ a: number; b: number }> = [];
  let a = from;
  while (a <= to) {
    const remaining = to - a + 1;
    if (remaining < min + min + 1) {
      // Absorb the tail rather than leaving a sliver behind.
      if (out.length > 0 && remaining < min) out[out.length - 1]!.b = to;
      else out.push({ a, b: to });
      break;
    }
    const w = Math.min(rng.int(min, max), remaining);
    out.push({ a, b: a + w - 1 });
    a += w + 1;
  }
  return out;
}

/** A wide opening through a shared wall, centred on the overlap of two chambers. */
function arch(site: GenSite, line: number, from: number, to: number, vertical: boolean): void {
  const { grid, rng } = site;
  const overlap = to - from;
  if (overlap < 3) return;
  const width = Math.min(overlap - 1, rng.chance(0.4) ? rng.int(4, 6) : 3);
  const start = from + (((overlap - width) / 2) | 0);
  for (let k = 0; k < width; k++) {
    if (vertical) grid.set(line, start + k, Cell.Doorway);
    else grid.set(start + k, line, Cell.Doorway);
  }
}

/**
 * Pools, colonnades and causeways. A chamber gets at most two pools and always
 * keeps a dry walkway around the outside, so the water is something you choose
 * to step into.
 */
function dressChamber(site: GenSite, r: Rect, depth: number): void {
  const { grid, rng } = site;
  // Deeper water gets a wider dry margin: wading everywhere is tedious, and the
  // tile you walk on is half of what makes the water look like water.
  const walkway = depth > 0.2 ? 3 : 2;

  if (depth > 0 && r.w >= MIN_CHAMBER + 2 && r.h >= MIN_CHAMBER + 2 && rng.chance(0.62)) {
    const pool = inset(r, walkway);
    const split = pool.w >= 11 && rng.chance(0.6);
    const splitV = pool.h >= 11 && !split && rng.chance(0.6);
    // Chamber first, pools second: the pools must win the region lookup.
    addRegion(site, r, 'atrium');
    if (split) {
      const half = (pool.w - 2) >> 1;
      addPool(site, { x: pool.x, y: pool.y, w: half, h: pool.h });
      addPool(site, { x: pool.x + half + 2, y: pool.y, w: pool.w - half - 2, h: pool.h });
    } else if (splitV) {
      const half = (pool.h - 2) >> 1;
      addPool(site, { x: pool.x, y: pool.y, w: pool.w, h: half });
      addPool(site, { x: pool.x, y: pool.y + half + 2, w: pool.w, h: pool.h - half - 2 });
    } else {
      addPool(site, pool);
    }
    return;
  }

  // Dry chambers alternate between colonnades and plain tiled halls.
  if (r.w >= 10 && r.h >= 10 && rng.chance(0.45)) {
    pillarLattice(grid, r, rng.int(3, 4), 2);
    addRegion(site, r, 'pillar');
  } else {
    addRegion(site, r, r.w * r.h >= 200 ? 'atrium' : 'hall');
  }
  if (r.w >= 9 && r.h >= 9 && rng.chance(0.45)) carveAlcove(site, r);
}

/**
 * A changing room in the corner of a dry chamber. The level needs somewhere to
 * put things: without these it is one continuous open space and every supply
 * would be lying in the middle of a floor.
 */
function carveAlcove(site: GenSite, r: Rect): void {
  const { grid, rng } = site;
  const w = rng.int(3, 4);
  const h = rng.int(3, 4);
  const left = rng.chance(0.5);
  const top = rng.chance(0.5);
  const a: Rect = {
    x: left ? r.x : r.x + r.w - w,
    y: top ? r.y : r.y + r.h - h,
    w,
    h,
  };
  strokeRect(grid, a, Cell.Wall);
  fillRect(grid, inset(a, 1), Cell.Floor);
  // One opening, on whichever inner face has the chamber behind it.
  if (rng.chance(0.5)) grid.set(left ? a.x + w - 1 : a.x, a.y + (h >> 1), Cell.Doorway);
  else grid.set(a.x + (w >> 1), top ? a.y + h - 1 : a.y, Cell.Doorway);
  addRegion(site, inset(a, 1), 'closet');
}

function addPool(site: GenSite, r: Rect): void {
  if (r.w < 2 || r.h < 2) return;
  fillRect(site.grid, r, Cell.Water);
  addRegion(site, r, 'flooded');
}

/**
 * Mirror the built half onto the other. The seam needs no special handling: the
 * last chamber column ends on the centre line, so it and its reflection meet as
 * one double-width room.
 */
function mirrorHalf(site: GenSite, mid: number): void {
  const { grid } = site;
  const w = grid.w;
  for (let y = 0; y < grid.h; y++) {
    const row = y * w;
    for (let x = 0; x < mid; x++) grid.cells[row + (w - 1 - x)] = grid.cells[row + x]!;
  }
  const count = site.regions.length;
  for (let i = 0; i < count; i++) {
    const r = site.regions[i]!;
    if (r.x >= mid) continue;
    addRegion(site, { x: w - r.x - r.w, y: r.y, w: r.w, h: r.h }, r.kind);
  }
}
